import { ensureDevice, fetchState, putSchedule, storedToken, subscribeToPush } from '../api/client'
import { remember } from '../api/outbox'
import type { Reminder } from '../core/reminders'
import type { WakeState } from '../core/types'
import { windowUpdatedAt } from '../stores/window'
import { deliveryMode, serverReachable } from './delivery'
import { currentKid, KidStorage, NO_KID } from '../stores/children'
import { LocalStorage, type Storage } from './storage'
import type { Platform } from './platform'

export { deliveryMode, serverReachable }

/**
 * Площадка «сайт»: localStorage, Web Push через сервер-планировщик, Wake Lock как запасной режим.
 *
 * Если сервер недоступен, приложение не делает вид, что уведомления придут: режим доставки
 * переключается на `screen`, экран удерживается включённым, а расписание ставится в очередь
 * и уходит при следующей удачной попытке.
 */

let registration: ServiceWorkerRegistration | null = null
let wakeLock: WakeLockSentinel | null = null
let pending: WakeState | null = null
let lastState: WakeState | null = null
/** Напоминания к последнему состоянию: уезжают с каждым расписанием, в том числе с досылкой. */
let lastReminders: Reminder[] = []
/** Подписка, которую ещё не приняли на сервере: она уезжает вместе с расписанием. */
let pendingSubscription: PushSubscriptionJSON | null = null
/**
 * Данные малыша лежат под приставкой `kid.<id>.`, общие ключи — без неё.
 * Сторы работают с прежними именами, просто хранилище знает, про кого речь.
 *
 * Основа подменяемая: в приложении из магазина данные лежат не в localStorage,
 * а в настройках Android через мост. Раньше нативная площадка отдавала своё
 * хранилище — без приставки, — а список малышей всё равно писался сюда, в localStorage.
 * Из-за этого на Android второй малыш затирал первого (проверено на эмуляторе 23.09.2026).
 */
let base: Storage = new LocalStorage()
// Пока малыша нет, хранилище смотрит в пустую приставку: номер ребёнку даёт только сервер
// (решение заказчика 28.10.2026), телефон его не придумывает даже как заглушку
let storage = new KidStorage(base, NO_KID)

/** Основное хранилище устройства — общее для всех малышей. */
export function deviceStorage(): Storage {
  return base
}

/**
 * Сменить основу. Звать до чтения списка малышей: иначе список ляжет в одно
 * хранилище, а данные — в другое.
 */
export function useDeviceStorage(next: Storage): void {
  base = next
  storage = new KidStorage(next, currentKid.value ?? NO_KID)
}

/** Хранилище приложения: то же самое, но повёрнутое к текущему малышу. */
export function appStorage(): Storage {
  return storage
}

/** Переключить приложение на другого малыша. */
export function useKid(id: string): void {
  storage.switchTo(id)
}


export function createWebPlatform(): Platform {
  return {
    storage,

    async stateChanged(state, reminders = []) {
      lastState = state
      lastReminders = reminders
      await send(state)
    },

    vibrate(double) {
      try {
        navigator.vibrate?.(double ? [0, 250, 150, 250] : [0, 250])
      } catch {
        // вибрации может не быть — это не повод падать
      }
    },

    keepAwake(on) {
      void toggleWakeLock(on)
    },

    notifications: {
      get background() {
        return deliveryMode.value === 'push'
      },
      disable: disableNotifications,
      async enable() {
        if (registration === null) {
          console.warn('Уведомления не включились: служебный скрипт не зарегистрирован')
          return false
        }
        try {
          // Спрашиваем первым делом и без единого await до этого: браузер разрешает
          // спрашивать только сразу после нажатия, а обращение к серверу это право съедает.
          // Если разрешение уже дано, не переспрашиваем: повторный вопрос в некоторых
          // браузерах возвращает «ещё не решено» и рушит включение на ровном месте.
          const permission =
            Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission()
          if (permission !== 'granted') {
            console.warn('Уведомления не включились: разрешение —', permission)
            deliveryMode.value = permission === 'denied' ? 'blocked' : 'screen'
            return false
          }

          await ensureDevice()
          const subscription = await subscribeToPush(registration)
          if (typeof subscription === 'string') {
            console.warn('Уведомления не включились:', subscription)
            deliveryMode.value = subscription === 'denied' ? 'blocked' : 'screen'
            return false
          }
          // Подписку принимает тот же запрос, что и расписание: отдельного маршрута на сервере нет.
          pendingSubscription = subscription
          deliveryMode.value = 'push'
          // Раньше отправляли, только если окно уже трогали в этот заход, — и подписка
          // оставалась на телефоне. Теперь площадке всё равно, было действие или нет.
          if (lastState !== null) await send(lastState)
          else await onNeedState?.()
          serverReachable.value = true
          return true
        } catch (error) {
          // Молчаливый отказ здесь уже один раз стоил неработающих уведомлений:
          // подписка не создавалась, а человек видел просто «ничего не произошло».
          console.warn("Не удалось включить уведомления:", error)
          serverReachable.value = false
          deliveryMode.value = 'screen'
          return false
        }
      },
    },
  }
}

/** Регистрирует Service Worker и определяет, как сейчас доставляются уведомления. */
export async function initWebPlatform(): Promise<void> {
  if ('serviceWorker' in navigator) {
    try {
      registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
    } catch {
      registration = null
    }
  }

  if (typeof Notification === 'undefined' || registration === null) {
    deliveryMode.value = 'screen'
    return
  }
  if (Notification.permission === 'denied') {
    deliveryMode.value = 'blocked'
    return
  }
  const subscription = await registration.pushManager?.getSubscription().catch(() => null)
  deliveryMode.value = subscription != null && Notification.permission === 'granted' ? 'push' : 'screen'
  // Подписка могла не доехать до сервера: она отдаётся вместе с расписанием, а расписание
  // уходит только при действии с окном. Держим её наготове, пока сервер не подтвердит.
  if (deliveryMode.value === 'push' && subscription != null) pendingSubscription = subscription.toJSON()
}

/**
 * Знает ли сервер о нашей подписке. Спрашиваем один раз при запуске: дешёвый GET,
 * зато человек не остаётся без уведомлений из-за того, что просто не трогал таймер.
 */
export async function pushUnknownToServer(): Promise<boolean> {
  if (pendingSubscription === null) return false
  try {
    const state = await fetchState(Date.now(), currentKid.value ?? '')
    return state === null || state.delivery !== 'push'
  } catch {
    return false
  }
}

/**
 * Сказать серверу, что безымянного расписания устройства больше нет.
 *
 * До переезда на нескольких малышей расписание лежало без ключа, и его задания живут
 * своей жизнью. Если переезд застал идущее окно, человек получал два одинаковых
 * уведомления об одном ребёнке: одно от старой записи, другое от новой.
 *
 * Серверу решать это нельзя: за тем же устройством может стоять клиент постарше,
 * который имён не присылает, и безымянная запись — его. Поэтому убирает тот, кто
 * точно знает, что переехал, — мы. Пустое окно стирает и запись, и её задания.
 *
 * Возвращает false, если не вышло: метка останется, попробуем при следующем запуске.
 */
/**
 * Снять с сервера расписание малыша под старым номером.
 *
 * Номер меняется один раз — при переезде на серверный. Задания, заведённые под прежним
 * ключом, об этом не знают и пришли бы вторым таким же уведомлением.
 */
export async function forgetKidSchedule(childKey: string, program: WakeState['program']): Promise<void> {
  if (storedToken() === null || childKey === '') return
  try {
    await putSchedule({ program, windowStartMillis: null }, Date.now(), Date.now(), undefined, childKey)
  } catch {
    // Не вышло — задания снимет следующее расписание этого устройства
  }
}

export async function forgetUnnamedSchedule(program: WakeState['program']): Promise<boolean> {
  if (storedToken() === null) return true // устройство серверу и не представлялось
  try {
    await putSchedule({ program, windowStartMillis: null }, Date.now(), Date.now(), undefined, '')
    return true
  } catch {
    return false
  }
}

async function send(state: WakeState): Promise<void> {
  if (deliveryMode.value !== 'push') {
    // Без подписки серверу нечего доставлять: держим экран, пока окно идёт.
    void toggleWakeLock(state.windowStartMillis !== null)
    return
  }
  try {
    const updatedAt = await windowUpdatedAt(storage)
    const subscription = pendingSubscription ?? undefined
    // Расписание заведено на пару «устройство + малыш»: у соседнего своё окно, и оно идёт само.
    const result = await putSchedule(state, updatedAt || Date.now(), Date.now(), subscription, currentKid.value ?? '', lastReminders)
    pendingSubscription = null
    serverReachable.value = true
    pending = null
    void toggleWakeLock(false)
    if (!result.applied && result.state !== null) onServerState?.(result.state)
  } catch {
    serverReachable.value = false
    pending = state
    void toggleWakeLock(state.windowStartMillis !== null)
  }
}

/**
 * Что делать, когда сервер отвечает своим состоянием: его запись оказалась свежее
 * (нажали кнопку под сообщением бота). Обработчик ставит слой состояния.
 */
type ServerStateHandler = (state: import('../api/client').ServerState) => void
let onServerState: ServerStateHandler | null = null
export function handleServerState(handler: ServerStateHandler | null): void {
  onServerState = handler
}

/**
 * Выключить уведомления: сначала браузер, потом сервер.
 *
 * Без отписки на сервере он продолжит слать в никуда, пока служба доставки не ответит
 * «подписки нет» — а человек в это время думает, что выключил.
 */
export async function disableNotifications(): Promise<void> {
  pendingSubscription = null
  deliveryMode.value = 'screen'
  try {
    const registration = await navigator.serviceWorker?.ready
    const subscription = await registration?.pushManager?.getSubscription()
    await subscription?.unsubscribe()
  } catch {
    // Браузер не дал отписаться — сервер всё равно попросим забыть
  }
  // Через очередь: человек мог выключить уведомления без сети, и сервер обязан
  // узнать об этом потом, а не продолжать слать.
  await remember('push_off')
}

/**
 * Кто расскажет площадке текущее состояние, когда оно понадобилось ей самой.
 * Ставит приложение при запуске: состояние знает стор, а не площадка.
 */
let onNeedState: (() => Promise<void>) | null = null
export function provideState(fn: () => Promise<void>): void {
  onNeedState = fn
}

/** Повторяет отправку расписания, когда сеть вернулась или страница снова видима. */
export function retryPending(): void {
  if (pending !== null) void send(pending)
}

/** Спрашивает сервер, не изменилось ли окно, пока вкладка была закрыта. */
export async function pullServerState(): Promise<void> {
  try {
    const state = await fetchState(Date.now(), currentKid.value ?? '')
    serverReachable.value = true
    if (state !== null) onServerState?.(state)
  } catch {
    serverReachable.value = false
  }
}

async function toggleWakeLock(on: boolean): Promise<void> {
  try {
    if (on) {
      if (wakeLock === null && 'wakeLock' in navigator) wakeLock = await navigator.wakeLock.request('screen')
    } else if (wakeLock !== null) {
      await wakeLock.release()
      wakeLock = null
    }
  } catch {
    wakeLock = null
  }
}
